const PAGINATION_TYPES = {
  STATIC: 'STATIC',
  LOAD_MORE: 'LOAD_MORE',
}

class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

class DataFetcher {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch (url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.#fetchData(url, section)
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async #fetchData (url, section) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.cache.set(url, html)

    return html
  }
}

class BasePaginationRenderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  render (document, event) {
    this.renderProductGrid(document, event)
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderProductGrid (document, event) {
    this.element.throwError(`${this.constructor.name}.render not implemented`)
  }
}

class StaticPaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (document) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    this.element.log(`${this.constructor.name}.renderProductGrid`, {
      productGrid,
      document,
    })

    productGrid.innerHTML = window.BAO.Utils.getElement(
      this.element.els['product-grid'].selector,
      { context: document },
    ).innerHTML
  }
}

class LoadMorePaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (html, e) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    const container = this.element.els['item-container']?.element
    if (!container) return

    const direction = this.getPaginationDirection(e)
    const newItems = document.createDocumentFragment()
    const newElements = window.BAO.Utils.getElements(
      `[${this.element._elIdentifier}="item"]`,
      {
        context: html,
      },
    )

    newElements.forEach((el) => newItems.appendChild(el.cloneNode(true)))

    if (direction == null) {
      container.innerHTML = ''
      container.append(newItems)
    } else if (direction === 'PREVIOUS') {
      container.prepend(newItems)
    } else if (direction === 'NEXT') {
      container.append(newItems)
    }

    this.renderLoadPrevious(html)
    this.renderLoadMore(html)

    this.element
      .querySelectorAll(`[${this.element._elIdentifier}="item"] img[srcset]`)
      .forEach((image) => {
        // eslint-disable-next-line no-self-assign
        image.outerHTML = image.outerHTML
      })

    this.updatePanelVisibility(this.hasProductsInContainer())
  }

  hasProductsInContainer () {
    return this.element.querySelectorAll(
      '.clc-List_Item:not(.clc-List_Item-promoted)',
    ).length > 0
  }

  updatePanelVisibility (hasProducts) {
    const productPanel = this.element.querySelector(
      `[${this.element._elIdentifier}="productpanel"]`,
    )
    const emptyPanel = this.element.querySelector(
      `[${this.element._elIdentifier}="emptypanel"]`,
    )

    if (productPanel) {
      productPanel.setAttribute('aria-hidden', (!hasProducts).toString())
    }

    if (emptyPanel) {
      emptyPanel.setAttribute('aria-hidden', hasProducts.toString())
    }

    if (this.element.dataset.template === 'search') {
      this.element.classList.toggle('clc-ProductGrid-searchEmpty', !hasProducts)
    }
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    if (typeof e === 'undefined') return null

    const loadPreviousSelector = this.element.els['load-previous']?.selector
    const loadMoreSelector = this.element.els['load-more']?.selector

    if (!loadPreviousSelector && !loadMoreSelector) return null

    const paginationElement = e.target.closest(
      [loadPreviousSelector, loadMoreSelector].filter(Boolean).join(', '),
    )

    if (!paginationElement) return null

    return paginationElement === this.element.els['load-previous']?.element
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    if (!this.shouldShowLoadPrevious && this.element.els['load-previous']) {
      return (this.element.els['load-previous'].element.innerHTML = '')
    }

    if (!this.shouldRerenderLoadPrevious) return

    if (this.element.els['load-previous']) {
      this.element.els['load-previous'].element.innerHTML =
        window.BAO.Utils.getElement(
          this.element.els['load-previous'].selector,
          { context: html },
        ).innerHTML
    }
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    if (!this.shouldRerenderLoadMore) return

    if (this.element.els['load-more']) {
      this.element.els['load-more'].element.innerHTML =
        window.BAO.Utils.getElement(this.element.els['load-more'].selector, {
          context: html,
        }).innerHTML
    }
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!window.BAO.Utils.getElement(
      `${this.element.els.item?.selector}[data-page="1"]`,
    )

    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage <= this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.element.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentLowestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.Utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return Number(productGridItems[0].dataset.page) || 1
  }

  get currentHighestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.Utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return (
      Number(productGridItems[productGridItems.length - 1].dataset.page) || 1
    )
  }
}

class Renderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
    this.paginationRenderer = this.getPaginationRenderer()
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} [event]
   *
   * @returns {void}
   */
  async render (html, event) {
    const newHTMLDom = new DOMParser().parseFromString(html, 'text/html')

    if (this.element.dataset.template === 'search' && !event) {
      this.paginationRenderer.render(newHTMLDom, event)
      this.renderSearchState(newHTMLDom)
      this.renderResultsCount(newHTMLDom)

      reinitCollectionSwymButtons()

      this.restoreFocus()

      this.element.loading = false

      return
    }

    this.renderFacets(newHTMLDom, event)
    this.renderFacetHeaders(newHTMLDom)
    this.renderSelectedFacetsCount(newHTMLDom, event)
    this.renderActiveFacets(newHTMLDom)
    this.paginationRenderer.render(newHTMLDom, event)
    this.renderActiveFilterCount(newHTMLDom)
    this.renderResultsCount(newHTMLDom)
    this.renderSearchState(newHTMLDom)

    reinitCollectionSwymButtons()

    this.restoreFocus()

    this.element.loading = false
  }

  restoreFocus () {
    const restoreId = this.element.dataset.restoreFocusId
    if (!restoreId) return

    this.element.querySelector(`#${CSS.escape(restoreId)}`)?.focus()

    delete this.element.dataset.restoreFocusId
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderFacetHeaders (document) {
    const facetHeaderEls = this.getEls('facet-header')
    if (!facetHeaderEls?.exists) return

    facetHeaderEls.elements.forEach((header) => {
      const selector = `${facetHeaderEls.selector}[data-index="${header.dataset.index}"]`
      const newHeader = window.BAO.Utils.getElement(selector, {
        context: document,
      })

      if (!newHeader) return

      header.outerHTML = newHeader.outerHTML
    })

    const filterAccordions = window.BAO.Utils.getElement('filter-accordions')
    if (filterAccordions) filterAccordions.bindTriggers()
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderFacets (document, event) {
    const facetEls = this.getEls('facet')
    if (!facetEls?.exists) return

    const getFacetSelector = (facet) =>
      `${facetEls.selector}[data-index="${facet.dataset.index}"]`

    const currentFacetElements = facetEls.elements
    const facetThatSubmitted =
      event?.submitter?.closest(facetEls.selector) ??
      event?.target?.closest(facetEls.selector)

    const facetsToRenderSimply = event
      ? currentFacetElements.filter((facet) => facet !== facetThatSubmitted)
      : currentFacetElements

    this.element.log('renderFacets', {
      currentFacetElements,
      facetThatSubmitted,
      facetsToRenderSimply,
    })

    facetsToRenderSimply.forEach((facet) => {
      const selector = getFacetSelector(facet)

      const el = window.BAO.Utils.getElement(selector, { context: document })
      if (!el) {
        this.element.throwError(
          `Could not find element with selector ${selector}`,
        )
      }

      facet.innerHTML = el.innerHTML
    })

    const filterAccordions = window.BAO.Utils.getElement('filter-accordions')
    if (filterAccordions) filterAccordions.updateTabbableElements()
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderSelectedFacetsCount (document, event) {
    const facetsSelectedEls = this.getEls('facets-selected')
    if (!facetsSelectedEls?.exists) return

    const buildSelectorForFacet = (facet) => {
      const selectorParts = {
        class: `.${Array.from(facet.classList).join('.')}`,
        additional: `${facetsSelectedEls.selector}[data-index="${facet.dataset.index}"]`,
      }

      return Object.values(selectorParts).join('')
    }

    const currentFacetCountElements = facetsSelectedEls.elements
    const submittedFacetElement = event?.submitter?.closest(
      facetsSelectedEls.selector,
    )

    const facetsToUpdate = event
      ? currentFacetCountElements.filter((el) => el !== submittedFacetElement)
      : currentFacetCountElements

    facetsToUpdate.forEach((facet) => {
      const selector = buildSelectorForFacet(facet)

      const newFacet = window.BAO.Utils.getElement(selector, {
        context: document,
      })
      if (!newFacet) {
        this.element.throwError(
          `Could not find element with selector ${selector}`,
        )
      }

      facet.outerHTML = newFacet.outerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderActiveFacets (document) {
    const activeFacetsEls = this.getEls('active-facets')
    if (!activeFacetsEls?.exists) return

    const activeFacets = activeFacetsEls.elements

    activeFacets.forEach((activeFacet) => {
      const selectorParts = {
        class: `.${Array.from(activeFacet.classList).join('.')}`,
        id: `#${activeFacet.id}`,
        additional: activeFacetsEls.selector,
      }
      if (activeFacets.length > 1 && !activeFacet.id) {
        throw new Error('Multiple active facets found, so all require an ID')
      }
      const selector = Object.values(selectorParts).join('')
      const activeFacetElement = document.querySelector(selector)

      this.element.log('renderActiveFacets', {
        parsedHTML: document,
        activeFacetElement,
        selector,
      })

      if (!activeFacetElement) return

      const currentEl = window.BAO.Utils.getElement(selector)
      if (!currentEl) return

      const hasActiveItems = !!window.BAO.Utils.getElement(
        this.getFacetItemSelector(),
        { context: activeFacetElement },
      )

      activeFacet.dataset.empty = (!hasActiveItems).toString()

      currentEl.innerHTML = activeFacetElement.innerHTML
    })
  }

  /**
   * @param {Document} htmlDocument
   *
   * @returns {void}
   */
  renderSearchState (htmlDocument) {
    if (this.element.dataset.template !== 'search') return

    const sectionId = this.element.dataset.sectionId
    const collectionFiltersSelector = `collection-filters#${CSS.escape(
      this.element.id,
    )}`
    const newCollectionFilters =
      htmlDocument.querySelector(collectionFiltersSelector) ??
      htmlDocument.querySelector('collection-filters')

    if (!newCollectionFilters) return

    const hasProductsInResponse = !!newCollectionFilters.querySelector(
      `[${this.element._elIdentifier}="item"]`,
    )
    const hasProductsInPage = this.element.querySelectorAll(
      `[${this.element._elIdentifier}="item"]`,
    ).length > 0
    const hasProducts = hasProductsInResponse || hasProductsInPage

    this.element.classList.toggle('clc-ProductGrid-searchEmpty', !hasProducts)

    const emptyPanel = this.element.querySelector(
      `[${this.element._elIdentifier}="emptypanel"]`,
    )
    const newEmptyPanel = newCollectionFilters.querySelector(
      `[${this.element._elIdentifier}="emptypanel"]`,
    )

    if (emptyPanel && newEmptyPanel) {
      emptyPanel.innerHTML = newEmptyPanel.innerHTML
    }

    const productPanel = this.element.querySelector(
      `[${this.element._elIdentifier}="productpanel"]`,
    )

    if (productPanel) {
      productPanel.setAttribute('aria-hidden', (!hasProducts).toString())
    }

    if (emptyPanel) {
      emptyPanel.setAttribute('aria-hidden', hasProducts.toString())
    }

    if (!hasProducts) return

    const newHeader = newCollectionFilters.querySelector(
      '.clc-ProductGrid_Header',
    )
    const currentHeader = this.element.querySelector('.clc-ProductGrid_Header')

    if (newHeader && currentHeader) {
      currentHeader.innerHTML = newHeader.innerHTML
    }

    const newFooter = newCollectionFilters.querySelector(
      '.clc-ProductGrid_Footer',
    )
    const currentFooter = this.element.querySelector('.clc-ProductGrid_Footer')

    if (newFooter && currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML
    }

    const facetDrawerSelector = `site-drawer[data-collection-filters-section-id="${sectionId}"]`
    const newDrawerInner = newCollectionFilters.querySelector(
      `${facetDrawerSelector} .drw-Drawer_Inner`,
    )
    const currentDrawer = window.document.querySelector(facetDrawerSelector)
    const currentDrawerInner = currentDrawer?.querySelector('.drw-Drawer_Inner')

    if (newDrawerInner && currentDrawerInner) {
      currentDrawerInner.innerHTML = newDrawerInner.innerHTML
    }

    const filterAccordions = currentDrawer?.querySelector('filter-accordions')

    if (filterAccordions) {
      filterAccordions.bindTriggers()
      filterAccordions.updateTabbableElements()
    }

    const newBannerTitle = htmlDocument.querySelector(
      '.clc-Banner-search .clc-Banner_Title',
    )
    const currentBannerTitle = window.BAO.Utils.getElement(
      '.clc-Banner-search .clc-Banner_Title',
    )

    if (newBannerTitle && currentBannerTitle) {
      currentBannerTitle.innerHTML = newBannerTitle.innerHTML
    }
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderActiveFilterCount (document) {
    const existingFilterCounts = window.BAO.Utils.getElements(
      this.element.els['filter-count']?.selector,
    )

    const newFilterCount = window.BAO.Utils.getElement(
      this.element.els['filter-count']?.selector,
      { context: document },
    )
    if (!newFilterCount) return

    existingFilterCounts.forEach(
      (element) => (element.innerHTML = newFilterCount.innerHTML),
    )
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderResultsCount (document) {
    const existingResultsCounts = window.BAO.Utils.getElements(
      this.element.externalEls['results-count']?.selector,
    )

    const newResultsCount = window.BAO.Utils.getElement(
      this.element.externalEls['results-count']?.selector,
      { context: document },
    )
    if (!newResultsCount) return

    existingResultsCounts.forEach(
      (element) => (element.innerHTML = newResultsCount.innerHTML),
    )
  }

  getPaginationRenderer () {
    switch (this.paginationType) {
      case PAGINATION_TYPES.STATIC:
        return new StaticPaginationRenderer(this.element)
      case PAGINATION_TYPES.LOAD_MORE:
        return new LoadMorePaginationRenderer(this.element)
      default:
        return new StaticPaginationRenderer(this.element)
    }
  }

  /**
   * @param {string} name
   *
   * @returns {Object|undefined}
   */
  getEls (name) {
    const external = this.element.externalEls[name]

    if (external?.exists) return external

    return this.element.els[name]
  }

  /**
   * @returns {string}
   */
  getFacetItemSelector () {
    return `[${this.element._elIdentifier}="active-facet"], [${this.element._externalElIdentifier}="active-facet"]`
  }

  /**
   * @returns {'STATIC'|'LOAD_MORE'}
   */
  get paginationType () {
    if (!this.element.dataset.paginationType) return PAGINATION_TYPES.STATIC

    return this.element.dataset.paginationType
  }
}

export class CollectionFilters extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['product-grid']
  }

  fetcher = new DataFetcher()
  renderer = new Renderer(this)

  checkValidity () {
    if (!this.isConnected) return

    super.checkValidity()
  }

  connectedCallback () {
    if (this._hasBeenInitialised) return

    this.sections = [
      {
        id: this.dataset.sectionId,
        section: this.dataset.sectionType,
        selector: '.shopify-section',
      },
    ]

    this.checkValidity()
    super.connectedCallback()
    this.bindFiltersForm()
    this.sanitiseSearchParams()
  }

  sanitiseSearchParams () {
    const params = this.searchParams.current

    params.delete('section_id')

    if (this.dataset.template === 'search') {
      const previousType = new URLSearchParams(window.location.search).get('type')

      params.set('type', 'product')

      if (previousType !== 'product' && params.has('q')) {
        this.searchParams.current = params
        this.searchParams.previous = new URLSearchParams(params.toString())
        this.searchParams.initial = new URLSearchParams(params.toString())
        this.renderPage({ searchParams: params.toString(), updateURLHash: true })

        return
      }
    }

    const page = Number(params.get('page') || 1)
    const lastPage = Number(
      this.els['product-grid']?.element?.dataset.lastPage || 1,
    )

    if (page > lastPage) {
      params.delete('page')
      history.replaceState(
        { searchParams: params.toString() },
        '',
        `${window.location.pathname}${
          params.toString() ? `?${params.toString()}` : ''
        }`,
      )
    }

    this.searchParams.current = params
    this.searchParams.previous = new URLSearchParams(params.toString())
    this.searchParams.initial = new URLSearchParams(params.toString())

    const hasProducts =
      this.querySelectorAll('.clc-List_Item:not(.clc-List_Item-promoted)').length >
      0

    if (!hasProducts && params.get('page')) {
      params.delete('page')
      this.searchParams.current = params
      this.searchParams.previous = new URLSearchParams(params.toString())
      this.renderPage({ searchParams: params.toString(), updateURLHash: true })
    }
  }

  buildSectionFetchUrl (sectionId) {
    const params = new URLSearchParams(this.searchParams.current.toString())
    params.delete('section_id')

    if (this.dataset.template === 'search') {
      params.set('type', 'product')
    }

    const queryString = params.toString()

    return `${window.location.pathname}?section_id=${sectionId}${
      queryString ? `&${queryString}` : ''
    }`
  }

  /**
   * @param {HTMLFormElement} form
   *
   * @returns {string}
   */
  buildSearchParamsFromForm (form) {
    const params = new URLSearchParams(new FormData(form))
    const defaultSortBy = form.dataset.defaultSortBy

    if (defaultSortBy && params.get('sort_by') === defaultSortBy) {
      params.delete('sort_by')
    }

    if (this.dataset.template === 'search') {
      params.set('type', 'product')
    }

    return params.toString()
  }

  bindFiltersForm () {
    const form = this.filtersForm

    if (!form || !this.debouncedOnSubmit) return

    this.listeners.add(form, 'input', this.debouncedOnSubmit)
    this.listeners.add(form, 'change', this.debouncedOnSubmit)
  }

  get filtersForm () {
    return document.querySelector(`form[data-id="${this.dataset.sectionId}"]`)
  }

  constructor () {
    super()
    /**
     * sections - a list of sections that are related to this element. They can be updated by
     * calling the renderSections method with updated HTML
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: this.dataset.sectionId,
        section: this.dataset.sectionType,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onLinkClick = this.onLinkClick.bind(this)

    this.debouncedOnSubmit = window.BAO.Utils.debounce(
      (e) => this.onSubmit(e),
      500,
    )
    this.listeners.add(document, 'input', this.debouncedOnSubmit)
    this.listeners.add(document, 'change', this.debouncedOnSubmit)

    this.listeners.add(document, 'submit', this.onFormSubmit.bind(this))
    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(this, 'click', this.onPaginationClick.bind(this))
    this.listeners.add(document, 'click', this.onFacetRemoveClick.bind(this))
  }

  /**
   * @param {MouseEvent} event
   */
  onFacetRemoveClick (event) {
    const link = event.target.closest('[data-facet-remove-el="link"]')
    if (!link) return

    const sectionId =
      link.closest('[data-collection-filters-section-id]')?.dataset
        .collectionFiltersSectionId ??
      link.closest(`form[data-id="${this.dataset.sectionId}"]`)?.dataset.id

    if (sectionId !== this.dataset.sectionId) return

    if (this.loading) return

    event.preventDefault()
    this.onLinkClick(event)
  }

  /**
   * @param {MouseEvent} event
   */
  onLinkClick (event) {
    event.preventDefault()

    const link = event.target.closest('a')
    if (!link?.href) return

    const searchParams = new URLSearchParams(new URL(link.href).search)

    if (this.dataset.template === 'search') {
      searchParams.set('type', 'product')
    }

    this.renderPage({ searchParams: searchParams.toString() })
  }

  onSubmit (event) {
    const form = event.target.closest(`form[data-id="${this.dataset.sectionId}"]`)
    if (!form) return

    event.preventDefault()

    if (this.loading) return

    if (document.activeElement?.id) {
      this.dataset.restoreFocusId = document.activeElement.id
    }

    const searchParams = this.buildSearchParamsFromForm(form)

    this.renderPage({ searchParams, event })
  }

  onFormSubmit (event) {
    if (event.target.dataset.id !== this.dataset.sectionId) return

    event.preventDefault()

    const searchParams = this.buildSearchParamsFromForm(event.target)

    this.renderPage({ searchParams, event })

    if (window.BAO.MediaBreakpoints.isSm) return

    this.els['product-grid'].element.scrollIntoView({ behavior: 'smooth' })
  }

  /**
   * @param {PopStateEvent} event
   *
   * @returns {void}
   */
  onHistoryChange (event) {
    const searchParams = event.state.searchParams
    if (!searchParams) return

    if (searchParams.toString() === this.searchParams.previous.toString()) {
      return
    }

    this.renderPage({ searchParams, updateURLHash: false })
  }

  async onPaginationClick (event) {
    const loadMoreLink = event.target.closest(
      this.els?.['load-more-trigger']?.selector,
    )
    const loadPrevLink = event.target.closest(
      this.els?.['load-prev-trigger']?.selector,
    )

    if (!loadMoreLink && !loadPrevLink) return

    const anchor = event.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) {
      return
    }

    event.preventDefault()

    const searchParams = new URLSearchParams(anchor.search)
    const page = searchParams.get('page') ?? '1'
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', page)

    if (this.dataset.template === 'search') {
      currentSearchParams.set('type', 'product')
    }

    await this.renderPage({
      searchParams: currentSearchParams.toString(),
      event,
    })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {SubmitEvent} [event]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  async renderPage ({ searchParams, event, updateURLHash = true } = {}) {
    if (this.loading) return

    this.log('renderPage', { searchParams, event, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    if (updateURLHash) this.updateURLHash(this.searchParams.current)

    for (const section of this.sections) {
      const url = this.buildSectionFetchUrl(section.id)
      this.log('renderPageSections', { section, url })

      const html = await this.fetcher.fetch(url, section.id)

      await this.renderer.render(html, event)
    }
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    const params = new URLSearchParams(searchParams.toString())
    params.delete('section_id')

    history.pushState(
      { searchParams: params.toString() },
      '',
      `${window.location.pathname}${
        params.toString() ? `?${params.toString()}` : ''
      }`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    const isLoading = val.toString()

    this.setAttribute('aria-busy', isLoading)

    const drawer = document.querySelector(
      `site-drawer[data-collection-filters-section-id="${this.dataset.sectionId}"]`,
    )

    if (drawer) {
      drawer.setAttribute('aria-busy', isLoading)
    }
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters)
}

function reinitCollectionSwymButtons () {
  if (!window._swat?.initializeActionButtons) return

  window._swat.initializeActionButtons('.clc-List_Items')
}

document.addEventListener('DOMContentLoaded', () => {
  reinitCollectionSwymButtons()
})

class FacetSort extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    this.syncLabelFromUrl()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.handleClick)
    this.listeners.add(document, 'click', this.handleOutsideClick.bind(this))
    this.listeners.add(document, 'keyup', this.handleEscapeKey.bind(this))
  }

  handleClick (e) {
    const triggerEl = e.target.closest(this.els.trigger?.selector)
    if (triggerEl) {
      e.stopPropagation()
      this.toggleDropdown()
      return
    }

    const buttonEl = e.target.closest(this.els.button.selector)
    if (buttonEl && buttonEl.dataset.value && buttonEl.dataset.name) {
      this.updateValues(buttonEl.dataset.value, buttonEl.dataset.name)
      this.closeDropdown()
    }
  }

  handleOutsideClick (e) {
    if (!this.contains(e.target)) {
      this.closeDropdown()
    }
  }

  handleEscapeKey (e) {
    if (e.key === 'Escape') {
      this.closeDropdown()
    }
  }

  toggleDropdown () {
    const isOpen = this.triggerEl?.getAttribute('aria-expanded') === 'true'
    if (isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown () {
    this.triggerEl?.setAttribute('aria-expanded', 'true')
    this.panelEl?.removeAttribute('aria-hidden')
  }

  closeDropdown () {
    if (this.panelEl?.contains(document.activeElement)) {
      this.triggerEl?.focus()
    }

    this.triggerEl?.setAttribute('aria-expanded', 'false')
    this.panelEl?.setAttribute('aria-hidden', 'true')
  }

  get triggerEl () {
    return this.els.trigger?.element
  }

  get panelEl () {
    return this.els.panel?.element
  }

  syncLabelFromUrl () {
    const sortBy = new URLSearchParams(window.location.search).get('sort_by')
    const labelEl = this.els['current-label']?.element
    const buttons = this.els.button?.elements || []

    if (!labelEl) return

    if (!sortBy) {
      const placeholder = this.dataset.sortByPlaceholder
      if (placeholder) labelEl.textContent = placeholder

      buttons.forEach((button) => {
        button.removeAttribute('aria-current')
        button.classList.remove('is-active')
      })
      return
    }

    const selectedButton = buttons.find((button) => button.dataset.value === sortBy)

    if (!selectedButton) return

    labelEl.textContent = selectedButton.dataset.name || selectedButton.textContent.trim()

    buttons.forEach((button) => {
      if (button.dataset.value === sortBy) {
        button.setAttribute('aria-current', 'page')
        button.classList.add('is-active')
      } else {
        button.removeAttribute('aria-current')
        button.classList.remove('is-active')
      }
    })
  }

  updateValues (value, name) {
    // Update form value
    this.formSort.value = value

    // Update the label inside this dropdown's trigger button
    const currentLabel = this.els['current-label']?.element
    if (currentLabel) {
      currentLabel.innerHTML = name
    }

    // Update all other facet-sort current-label elements on the page
    document.querySelectorAll('[data-facet-sort-el="current-label"]').forEach((el) => {
      if (el !== currentLabel) el.innerHTML = name
    })

    // Dispatch event on form to trigger the update
    this.formSort.dispatchEvent(new Event('input', { bubbles: true }))

    // Update button active states within this dropdown
    this.els.button.elements.forEach((button) => {
      if (button.dataset.value === value) {
        button.setAttribute('aria-current', 'page')
        button.classList.add('is-active')
      } else {
        button.removeAttribute('aria-current')
        button.classList.remove('is-active')
      }
    })
  }

  get formSort () {
    const selector = '[data-collection-filters-form-el="sort"]'

    return document.querySelector(selector)
  }
}

if (!customElements.get('facet-sort')) {
  customElements.define('facet-sort', FacetSort)
}
